import React, { useEffect, useRef, useState } from "react";
import { BackHandler, Keyboard, View, useWindowDimensions } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import Popover from "react-native-popover-view/dist/Popover";

import { config } from "../../shared/environment";
//common component
import CommonHeader from "../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
//Service page
import AddEditCustomerDetails, { customerForwardRefProps } from "./addEditCustomerDetails";
import AddEditContactInfo, { contactInfoForwardRefProps } from "./addEditContactInfo";
//Type module
import { Code, CustomerDetailsProps, Name, customerAddressPropsType } from "./addEditCustomerModule";
import { useAppNavigation, useAppRoute } from "../../types/@react-navigation/navigation";
//Redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { pageDirty } from "../../redux/slices/commonSlice";
import { createCustomer, editCustomer, getOneCustomerDetails, sendCustomerEmail } from "../../redux/slices/customerSlice";
//Shared library
import { ZenButton, ZenLoader, ZenToast } from "zen-core-elements";
import { CustomModal } from "../../shared/commonComponents/customModal";

const AddEditCustomerScreen = () => {
    //Variable for Top tap navigation
    const TopTab = createMaterialTopTabNavigator()
    //variable to store windowsDimentions
    const { width } = useWindowDimensions()
    //variable to store useNavigation values 
    const navigation = useAppNavigation()
    //variable to store useRoute values 
    const { params } = useAppRoute("AddEditCustomer")
    const isActive = params?.routeName !== "InActive Customer"
    //variable to store redux dispatch 
    const dispatch = useAppDispatch()
    //variable to store current screen name 
    const [screenName, setScreenName] = useState('Customer Details')
    //variable to handle leave popover
    const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
    //variable to handle transparent loader
    const [transparentLoader, setTransparentLoader] = useState(false)
    //variable to handle loader
    const [isLoader, setIsLoader] = useState(true)
    //variable to store country name 
    const [countryName, setCountryName] = useState<Name[]>([])
    //variable to store country code
    const [countryCode, setCountryCode] = useState<Code[]>([])

    //useRef to handle customer details formik
    const handleCustomerRef = useRef<customerForwardRefProps>(null)
    //useRef to handle contact info formik 
    const handleContactinfoRef = useRef<contactInfoForwardRefProps>(null)

    const getCountry = useAppSelector((state) => state?.common?.getCountry)
    const { isContactInfoDirty, isCustomerDetailsDirty } = useAppSelector((state) => state?.customer)
    // //Variable to store customer details
    const customerDetails = useAppSelector((state) => state?.customer?.oneCustomerDetails)

    const [tabNavigation, setTabNavigation] = useState<{ navigate: (props: 'Contact Information') => {} }>();

    //variable to handle hardware back press
    const handleHardwareBackPress = (): boolean => {
        return (isCustomerDetailsDirty || isContactInfoDirty)
    }

    //UseEffect for handle Hardware BackPress
    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            if (handleHardwareBackPress()) {
                setLeavePopoverVisible(true)
                return true
            }
            navigation.goBack()
            return true
        });
        return () => backHandler?.remove()
    }, [handleHardwareBackPress])

    const handleHeaderBackPress = async () => {
        Keyboard?.dismiss()
        if (handleHardwareBackPress()) {
            setLeavePopoverVisible(true)
        }
        else {
            navigation?.goBack()
        }
    }
    // initial useEffect
    useEffect(() => {
        setIsLoader(true)
        if (params?.customerId) {
            getOneCustomerDetailsApi()
        } else {
            setIsLoader(false)
        }
    }, [params?.customerId])

    useEffect(() => {
        if (getCountry?.value) {
            let code: Code[] = []
            let name: Name[] = []
            getCountry?.value?.country?.map((countries) => {
                code?.push({ label: `+${countries?.countryCode} ${countries?.name}`, value: countries?.id })
                name?.push({ label: countries?.name, value: countries?.id, countryCode: `+${countries?.countryCode}`, formikvalue: countries })
            })
            setCountryCode(code)
            setCountryName(name)
        }
    }, [getCountry])

    //Method for get one customer details
    //isRefresh: refreshing the stored details after a save, the screen is already navigating back so it must not go back again
    const getOneCustomerDetailsApi = async (isGet?: boolean, isRefresh?: boolean) => {
        if (!params?.customerId) {
            setIsLoader(false)
            return
        }
        if (customerDetails?.customerDetails?.user?.userId !== params?.userId || isGet) {
            await dispatch(getOneCustomerDetails({ storeId: config?.storeId, customerId: params?.customerId!! })).unwrap().catch(() => {
                if (isRefresh) return
                ZenToast({
                    type: "error",
                    message: "Failed to get the customer details, Please try again later"
                })
                navigation?.goBack()
            }).finally(() => {
                setIsLoader(false)
            })
        } else {
            setIsLoader(false)
        }
    }
    //method for popover confirmation 
    const confirmation = (item: { id: number }) => {
        setLeavePopoverVisible(false)
        if (item?.id == 2) {
            navigation?.goBack()
            dispatch(pageDirty(false))
        }
    }

    const handleNavigation = () => {
        dispatch(pageDirty(false))
        navigation.goBack()
    }

    useEffect(() => {
        if (isContactInfoDirty || isCustomerDetailsDirty) {
            dispatch(pageDirty(true))
        }
        else {
            dispatch(pageDirty(false))
        }
    }, [isContactInfoDirty, isCustomerDetailsDirty])

    //Method for add or edit customer
    const addEditCustomer = async (detailsFormikProps: CustomerDetailsProps | null | "numberNotVerfied" | null, contactFormikProps: customerAddressPropsType[] | null) => {
        setTransparentLoader(true)
        let data = {} as CustomerDetailsProps
        if (typeof detailsFormikProps == "object") {
            data = {
                userInfo: { ...detailsFormikProps?.userInfo, "isPos": true, "isActive": true },
                customerEntry: detailsFormikProps?.customerEntry,
                contactInfo: contactFormikProps,
                isEmailMarketing: false,
                ...(detailsFormikProps?.phoneNumberChanged) && { phoneNumberChanged: detailsFormikProps?.phoneNumberChanged },
                ...(detailsFormikProps?.smsVerified) && { smsVerified: detailsFormikProps?.smsVerified }
            } as CustomerDetailsProps
        }
        // Edit customer
        if (params?.customerId) {
            await dispatch(editCustomer({ storeId: config?.storeId, customerId: customerDetails?.customerDetails?.user?.userId!!, data, isActive })).unwrap().then(() => {
                ZenToast({ type: "success", message: "Customer Edited successfully" })
                getOneCustomerDetailsApi(true, true)
                handleNavigation()
            }).catch((error) => {
                ZenToast({ type: "error", message: "Faild to Edit customer, please try again after some time!!" })
                console?.log("Edit customer error", error)
            }).finally(() => {
                setTransparentLoader(false)
            })
        }
        //Add customer
        else {
            await dispatch(createCustomer({ storeId: config?.storeId, data, isActive: true })).unwrap().then((response) => {
                if (response && response?.createCustomerDetails) {
                    ZenToast({ type: "success", message: "Customer created successfully" })
                    handleNavigation()
                }
                else {
                    ZenToast({ type: "error", message: "Faild to create customer" })
                }
            }).catch((error) => {
                ZenToast({ type: "error", message: "Faild to create customer, please try again after some time!!" })
                console?.log("Create customer error", error)
            }).finally(() => {
                setTransparentLoader(false)
            })
        }
    }

    const sendEmail = async (customerId: number) => {
        setTransparentLoader(true)
        await dispatch(sendCustomerEmail({ customerId, storeId: config?.storeId })).unwrap().then((response) => {
            if (response.success) {
                ZenToast({ type: "success", message: "Email successfully sent to the customer" })
            }
        }).catch((error) => {
            ZenToast({ type: "error", message: "Faild to sent a email to the customer." })
            console?.log("Delete customer api error", error)
        }).finally(() => {
            setTransparentLoader(false)
        })
    }

    const handleOnSave = async () => {
        const customerdetailsProps = handleCustomerRef?.current?.handleSave()
        const ContactinfoProps = handleContactinfoRef?.current?.handleSave()

        await Promise?.all([customerdetailsProps, ContactinfoProps]).then(([response1, response2]) => {
            if (response1 && !response2) {
                ZenToast({ type: "error", message: "Please fill out the mandatory fields in contact information" })
            }
            else if (!response1 && response2) {
                ZenToast({ type: "warning", message: "Please fill out the mandatory fields in customer details" })
            }
            else if (!response1 && !response2) {
                ZenToast({ type: "error", message: "Please fill out the mandatory fields in customer details and contact information" })
            }
            else if (response1 == "numberNotVerfied") {
                ZenToast({ type: "error", message: "Please verify your Phone Number" })
            }
            else {
                if (response1 != undefined && response2 != undefined) {
                    if (handleHardwareBackPress()) {
                        addEditCustomer(response1, response2)
                    }
                    else {
                        ZenToast({ type: "info", message: "You have no changes to save" })
                    }
                }
            }
        }).catch((error) => {
            console.log("promise error", error)
            ZenToast({ type: "error", message: "Please try again after some time!!" })
        })
    }

    const handleRightIconOnPress = async () => {
        if (screenName == 'Customer Details')
            tabNavigation?.navigate('Contact Information');
        else {
            Keyboard?.dismiss()
            await handleOnSave()
        }
    }
    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <View style={{ flex: 1 }} >
                    <CommonHeader headerName={params?.customerId ? "Edit Customer" : "Create Customer"} backIcon backIconOnpress={handleHeaderBackPress}
                        rightComponent={
                            <ZenButton
                                title={screenName == 'Customer Details' ? "Next" : "Save"}
                                titleColor={theme?.light}
                                disabled={false}
                                buttonStyle={{ backgroundColor: theme?.primary, borderRadius: 10, paddingHorizontal: 12 }}
                                onPress={handleRightIconOnPress}
                            />
                        } />
                    {isLoader ? <ZenLoader viewStyle={{ flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: theme?.lightBackgroundColor }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} /> :
                        <TopTab.Navigator
                            initialRouteName="Customer Details"
                            screenOptions={{
                                tabBarLabelStyle: { fontSize: theme.largerFontSize, fontFamily: theme.bold, textTransform: 'capitalize' },
                                tabBarIndicatorStyle: { backgroundColor: theme.primary },
                                tabBarActiveTintColor: theme.primary,
                                tabBarInactiveTintColor: theme.lightFontColor,
                                tabBarAndroidRipple: { borderless: false },
                            }}
                            backBehavior="none"
                        >

                            <TopTab.Screen
                                name="Customer Details"
                                options={{ tabBarLabel: 'Customer Details', title: "Customer Details", }}
                                listeners={() => ({
                                    focus: () => {
                                        setScreenName('Customer Details')
                                    }
                                })}
                            >
                                {(props) => {
                                    useEffect(() => {
                                        setTabNavigation(props.navigation);
                                    }, []);
                                    return (
                                        <AddEditCustomerDetails
                                            {...props}
                                            ref={handleCustomerRef}
                                            customerDetails={params?.customerId ? customerDetails?.customerDetails : undefined}
                                            countryCode={countryCode}
                                            isEdit={params?.customerId!!}
                                            isUserVerified={params?.isUserVerified || false}
                                            sendEmail={sendEmail} />
                                    )
                                }}
                            </TopTab.Screen>
                            <TopTab.Screen
                                name="Contact Information"
                                options={{ tabBarLabel: 'Contact Information', title: "Contact Information" }}
                                listeners={() => ({
                                    focus: () => {
                                        setScreenName('Contact Information')
                                    }
                                })}
                            >
                                {(props) => (
                                    <AddEditContactInfo
                                        {...props}
                                        ref={handleContactinfoRef}
                                        customerDetails={params?.customerId ? customerDetails?.customerDetails : undefined}
                                        countryName={countryName}
                                        isEdit={params?.customerId!!} />
                                )}
                            </TopTab.Screen>
                        </TopTab.Navigator>
                    }
                    <CustomModal visible={transparentLoader} transparent>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                    </CustomModal>
                    {/* For leave confirmation */}
                    <Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
                        <CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
                    </Popover>
                </View >
            )}
        </ThemeConsumer>

    )
}

export default AddEditCustomerScreen